import { Checkbox, Flex, Text, TextInput } from "@mantine/core";
import {
  type Control,
  Controller,
  type UseFormRegister,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import Autocomplete from "src/components/shared/_FormInputs/Autocomplete";
import DatePickerInput from "src/components/shared/_FormInputs/DatePickerInput";
import SegmentedControl from "src/components/shared/_FormInputs/SegmentedControl";
import Select from "src/components/shared/_FormInputs/Select";
import { Button } from "src/components/shared/Button/Button";
import { useGetApi } from "src/hooks";
import { type ISearchForm } from "src/pages/SearchPage/SearchPage.types";
import { mapSearchUsers } from "src/pages/SearchPage/SearchPage.utils";
import { getYachtId } from "src/utils";

import { SearchFiltersWrapper } from "./Search.styled";
import useNonMaritime from "../../../hooks/useNonMaritime";

interface Props {
  register: UseFormRegister<ISearchForm>;
  control: Control<ISearchForm>;
  onClear: () => void;
  isMedical?: boolean;
}

const SearchFilters = ({ register, control, onClear, isMedical }: Props) => {
  const { replaceMaritimeText } = useNonMaritime();
  const { collectionId } = useParams();
  const { data: users } = useGetApi({
    request: "search-user-list",
    mapper: mapSearchUsers,
    body: {
      params: {
        ...(collectionId ? { lbid: collectionId } : {}),
        yachtid: getYachtId(),
      },
    },
  });

  return (
    <SearchFiltersWrapper>
      <Flex direction="column" gap={16}>
        <Button p={10} onClick={onClear}>
          Clear Filters
        </Button>
        <Controller
          name="ref"
          control={control}
          render={({ field }) => (
            <TextInput
              label="Entry Ref. #"
              {...field}
              value={field.value || ""}
            />
          )}
        />
        <Autocomplete
          control={control}
          name="mention"
          label="Mention"
          data={users ?? []}
        />
        <Autocomplete
          control={control}
          name="opened"
          label="Opened By"
          data={users ?? []}
        />
        <Autocomplete
          control={control}
          name="urn"
          label="URNs"
          data={users ?? []}
        />
        <Select
          label="Status"
          data={[
            { value: "*", label: "All" },
            { value: "0", label: "Open" },
            { value: "3", label: replaceMaritimeText("Yacht Verified") },
            { value: "99", label: "Withdrawn" },
          ]}
          name="status"
          control={control}
        />
        <Autocomplete
          control={control}
          name={"explicit"}
          label={isMedical ? "Patient" : "Specific User"}
          data={users ?? []}
        />
        {isMedical && (
          <Autocomplete
            control={control}
            name={"med_condition" as any}
            label="Medical Contition"
            data={[]}
          />
        )}
        <DatePickerInput
          miw={150}
          valueFormat="D MMM YYYY"
          label="Start Date"
          name="startDate"
          control={control}
        />
        <DatePickerInput
          miw={150}
          valueFormat="D MMM YYYY"
          label="End Date"
          name="endDate"
          control={control}
        />
        <div>
          <Text size="sm" fw={500} mt={3}>
            Sort by date
          </Text>
          <SegmentedControl
            data={[
              { label: "Desc", value: "desc" },
              { label: "Asc", value: "asc" },
            ]}
            name="sort"
            control={control}
          />
        </div>
        <Checkbox label="Show Notes" {...register("sub")} />
      </Flex>
    </SearchFiltersWrapper>
  );
};

export default SearchFilters;
